import type { Effect } from "../engine/effects/effect";
import type { XYEffect } from "../engine/effects/xyEffect";
import type { Sound } from "../engine/things/sounds/sound";
import type { AnimationLoader } from "./animationLoader";
import { EffectManager } from "./effectManager";
import { SoundQueue } from "./sound/soundQueue";
import type { SoundManager } from "./sound/soundManager";
import { UnboundedEffectQueue } from "./unboundedEffectQueue";
import type { UnboundedEffectRenderer } from "./unboundedEffectRenderer";

/**
 * Bundles the three per-phase timeline systems (bounded effects, ordered sounds,
 * order-synced unbounded sprite effects) for one half (pre or pos) of a turn.
 * Wraps the underlying EffectManager / SoundQueue / UnboundedEffectQueue — does
 * not replace them; their identities are preserved.
 */
export class TurnPhase {
	readonly effects: EffectManager;
	private readonly sounds = new SoundQueue();
	private readonly unbounded = new UnboundedEffectQueue();

	constructor(animationLoader: AnimationLoader) {
		this.effects = new EffectManager(animationLoader);
	}

	async load(
		boundedEffects: Effect[],
		deferredSounds: Sound[],
		queuedUnbounded: XYEffect[],
		animationLoader: AnimationLoader,
	): Promise<void> {
		this.sounds.load(deferredSounds);
		await Promise.all([
			this.effects.addEffects(boundedEffects),
			this.unbounded.load(queuedUnbounded, animationLoader),
		]);
	}

	passTime(elapsed: number, soundManager: SoundManager, unboundedRenderer: UnboundedEffectRenderer): void {
		this.effects.passTime(elapsed);
		const order = this.effects.getCurrentAnimationOrder();
		this.sounds.releaseUpTo(order, soundManager);
		this.unbounded.releaseUpTo(order, unboundedRenderer);
	}

	flush(soundManager: SoundManager, unboundedRenderer: UnboundedEffectRenderer): void {
		this.sounds.releaseAll(soundManager);
		this.unbounded.releaseAll(unboundedRenderer);
	}

	isAllExpired(): boolean {
		return this.effects.isAllExpired();
	}

	getCurrentAnimationOrder(): number | null {
		return this.effects.getCurrentAnimationOrder();
	}

	clear(): void {
		this.effects.clearEffects();
		this.sounds.clear();
		this.unbounded.clear();
	}

	/** Level-transition board swap: drops queued unbounded without touching effects/sounds. */
	clearUnbounded(): void {
		this.unbounded.clear();
	}
}
